/**
 * engineering_canvas_lib.js — Shared helpers for SmartPlant-grade
 * interactive engineering editors (PFD, P&ID, SLD, …).
 *
 * Centralises:
 *   • visual constants (palette, grid, mono font)
 *   • coordinate snapping
 *   • screen→SVG conversion
 *   • nozzle position resolution
 *   • orthogonal-L stream path
 *   • default equipment palette (7 standard items)
 *   • default nozzles per equipment type
 */

export const GRID = 20;

export const COL = {
   bg:     "#020617",
   panel:  "#0f1729",
   border: "#1e293b",
   text:   "#e2e8f0",
   dim:    "#94a3b8",
   spark:  "#22d3ee",
   active: "#0F62FE",
   ok:     "#22c55e",
   warn:   "#f59e0b",
   err:    "#ef4444",
   accent: "#a78bfa",
};

export const mono = {
   fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
};

export const snap = (v) => Math.round(v / GRID) * GRID;

export const newId = (prefix) =>
   `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

/** Convert client coords → SVG world coords (accounts for zoom/pan). */
export function screenToSvg(svgEl, clientX, clientY, zoom, pan) {
   if (!svgEl) return { x: 0, y: 0 };
   const rect = svgEl.getBoundingClientRect();
   const cx = (clientX - rect.left) / zoom - pan.x;
   const cy = (clientY - rect.top) / zoom - pan.y;
   return { x: cx, y: cy };
}

/** Absolute (x, y, side) of a nozzle on an equipment. */
export function resolveNozzle(eq, nz_id) {
   const nz = (eq.nozzles || []).find((n) => n.id === nz_id)
      || (eq.nozzles || [])[0];
   const side = nz?.side || "right";
   const off = nz?.offset ?? 0.5;
   if (side === "left")   return { x: eq.x,         y: eq.y + eq.h * off, side };
   if (side === "right")  return { x: eq.x + eq.w,  y: eq.y + eq.h * off, side };
   if (side === "top")    return { x: eq.x + eq.w * off, y: eq.y,         side };
   return                       { x: eq.x + eq.w * off, y: eq.y + eq.h,  side };
}

/** Orthogonal-L SVG path between two nozzles (legacy, kept for callers
 *  that don't have obstacle context). */
export function orthogonalPath(a, b, fromNozzleId, toNozzleId) {
   if (!a || !b) return null;
   const na = resolveNozzle(a, fromNozzleId);
   const nb = resolveNozzle(b, toNozzleId);
   const midX = (na.x + nb.x) / 2;
   return `M ${na.x} ${na.y} L ${midX} ${na.y} L ${midX} ${nb.y} L ${nb.x} ${nb.y}`;
}

/**
 * Obstacle-aware orthogonal routing between two nozzles.
 *
 * Algorithm: BFS on a coarse grid (cell = GRID px). Each equipment is
 * inflated by `pad` and marked impassable except for the nozzle anchor
 * cells. The path is post-processed to collapse colinear runs so we
 * emit a clean polyline.
 *
 *   • Start / end cells = the nozzle positions.
 *   • Obstacles = every equipment in `equipmentList` other than the
 *     two endpoints' parents.
 *   • If no path found within `bbox`, falls back to the L-shape path.
 *
 * @param {object} a — source equipment {x, y, w, h, nozzles}
 * @param {object} b — target equipment
 * @param {string} fromNozzleId
 * @param {string} toNozzleId
 * @param {object[]} equipmentList — all equipment on the sheet
 * @param {number} [pad=14] — padding around each obstacle (px)
 * @returns {string} SVG path
 */
export function routeAroundObstacles(a, b, fromNozzleId, toNozzleId,
                                                  equipmentList, pad = 14) {
   if (!a || !b) return null;
   const na = resolveNozzle(a, fromNozzleId);
   const nb = resolveNozzle(b, toNozzleId);
   // Cell size = GRID for snap consistency
   const CELL = GRID;
   // Compute bbox (extra margin so we can route around)
   const margin = 80;
   const minX = Math.min(na.x, nb.x) - margin;
   const minY = Math.min(na.y, nb.y) - margin;
   const maxX = Math.max(na.x, nb.x) + margin;
   const maxY = Math.max(na.y, nb.y) + margin;
   const W = Math.ceil((maxX - minX) / CELL) + 1;
   const H = Math.ceil((maxY - minY) / CELL) + 1;
   // Safety cap — fall back if grid is huge (>40k cells)
   if (W * H > 40000) {
      return orthogonalPath(a, b, fromNozzleId, toNozzleId);
   }
   const toCell = (px, py) => ({
      cx: Math.round((px - minX) / CELL),
      cy: Math.round((py - minY) / CELL),
   });
   const fromCell = (cx, cy) => ({
      x: minX + cx * CELL,
      y: minY + cy * CELL,
   });
   // Build blocked grid
   const blocked = new Uint8Array(W * H);
   const cellIdx = (cx, cy) => cy * W + cx;
   const aId = a.id, bId = b.id;
   (equipmentList || []).forEach((eq) => {
      if (eq.id === aId || eq.id === bId) return;
      const x0 = eq.x - pad, y0 = eq.y - pad;
      const x1 = eq.x + eq.w + pad, y1 = eq.y + eq.h + pad;
      const c0 = toCell(x0, y0), c1 = toCell(x1, y1);
      for (let cy = Math.max(0, c0.cy); cy <= Math.min(H - 1, c1.cy); cy++) {
         for (let cx = Math.max(0, c0.cx); cx <= Math.min(W - 1, c1.cx); cx++) {
            blocked[cellIdx(cx, cy)] = 1;
         }
      }
   });
   const start = toCell(na.x, na.y);
   const end = toCell(nb.x, nb.y);
   if (start.cx < 0 || start.cy < 0 || end.cx >= W || end.cy >= H) {
      return orthogonalPath(a, b, fromNozzleId, toNozzleId);
   }
   blocked[cellIdx(start.cx, start.cy)] = 0;
   blocked[cellIdx(end.cx, end.cy)] = 0;
   // BFS — slight bias to prefer orthogonal Manhattan moves
   const parent = new Int32Array(W * H).fill(-1);
   const visited = new Uint8Array(W * H);
   const queue = [cellIdx(start.cx, start.cy)];
   visited[queue[0]] = 1;
   const endIdx = cellIdx(end.cx, end.cy);
   let found = false;
   let qi = 0;
   const DIRS = [[1, 0], [-1, 0], [0, 1], [0, -1]];
   while (qi < queue.length) {
      const cur = queue[qi++];
      if (cur === endIdx) { found = true; break; }
      const cx = cur % W, cy = (cur - cx) / W;
      for (const [dx, dy] of DIRS) {
         const nx = cx + dx, ny = cy + dy;
         if (nx < 0 || nx >= W || ny < 0 || ny >= H) continue;
         const ni = cellIdx(nx, ny);
         if (visited[ni] || blocked[ni]) continue;
         visited[ni] = 1;
         parent[ni] = cur;
         queue.push(ni);
      }
   }
   if (!found) {
      return orthogonalPath(a, b, fromNozzleId, toNozzleId);
   }
   // Trace back
   const pts = [];
   let cur = endIdx;
   while (cur !== -1) {
      const cx = cur % W, cy = (cur - cx) / W;
      const pt = fromCell(cx, cy);
      pts.push(pt);
      cur = parent[cur];
   }
   pts.reverse();
   // Collapse colinear runs
   const cleaned = [pts[0]];
   for (let i = 1; i < pts.length - 1; i++) {
      const prev = cleaned[cleaned.length - 1];
      const next = pts[i + 1];
      const cur2 = pts[i];
      const dx1 = cur2.x - prev.x, dy1 = cur2.y - prev.y;
      const dx2 = next.x - cur2.x, dy2 = next.y - cur2.y;
      const colinear = (dx1 === 0 && dx2 === 0) || (dy1 === 0 && dy2 === 0);
      if (!colinear) cleaned.push(cur2);
   }
   cleaned.push(pts[pts.length - 1]);
   // Snap endpoints back to the actual nozzle positions (the cell grid
   // may have rounded them)
   cleaned[0] = { x: na.x, y: na.y };
   cleaned[cleaned.length - 1] = { x: nb.x, y: nb.y };
   // Emit SVG path
   return cleaned.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
                       .join(" ");
}

/** Default nozzle layout per equipment type. */
const NOZZLE_DEFAULTS = {
   vessel: { w: 60, h: 120, nozzles: [
      { side: "top",    offset: 0.5, kind: "inlet" },
      { side: "bottom", offset: 0.5, kind: "outlet" },
      { side: "right",  offset: 0.5, kind: "vent" },
   ]},
   tank: { w: 100, h: 100, nozzles: [
      { side: "top",    offset: 0.5, kind: "inlet" },
      { side: "bottom", offset: 0.5, kind: "outlet" },
   ]},
   pump: { w: 60, h: 50, nozzles: [
      { side: "left",  offset: 0.5, kind: "suction" },
      { side: "right", offset: 0.5, kind: "discharge" },
   ]},
   compressor: { w: 70, h: 60, nozzles: [
      { side: "left",  offset: 0.5, kind: "suction" },
      { side: "right", offset: 0.5, kind: "discharge" },
   ]},
   heat_exchanger: { w: 120, h: 50, nozzles: [
      { side: "left",  offset: 0.3, kind: "shell_in" },
      { side: "right", offset: 0.3, kind: "shell_out" },
      { side: "left",  offset: 0.7, kind: "tube_in" },
      { side: "right", offset: 0.7, kind: "tube_out" },
   ]},
   column: { w: 60, h: 200, nozzles: [
      { side: "top",    offset: 0.5, kind: "ovhd" },
      { side: "bottom", offset: 0.5, kind: "btms" },
      { side: "left",   offset: 0.4, kind: "feed" },
      { side: "right",  offset: 0.6, kind: "reflux" },
   ]},
   reactor: { w: 80, h: 140, nozzles: [
      { side: "top",    offset: 0.5, kind: "feed" },
      { side: "bottom", offset: 0.5, kind: "product" },
      { side: "left",   offset: 0.3, kind: "catalyst" },
   ]},
   valve: { w: 30, h: 30, nozzles: [
      { side: "left",  offset: 0.5, kind: "in" },
      { side: "right", offset: 0.5, kind: "out" },
   ]},
   filter: { w: 60, h: 60, nozzles: [
      { side: "left",  offset: 0.5, kind: "in" },
      { side: "right", offset: 0.5, kind: "out" },
   ]},
   fired_heater: { w: 100, h: 100, nozzles: [
      { side: "left",  offset: 0.5, kind: "in" },
      { side: "right", offset: 0.5, kind: "out" },
      { side: "top",   offset: 0.5, kind: "stack" },
   ]},
};

export function defaultNozzlesFor(eqType) {
   const t = (eqType || "vessel").toLowerCase();
   const d = NOZZLE_DEFAULTS[t] || NOZZLE_DEFAULTS.vessel;
   return {
      w: d.w,
      h: d.h,
      nozzles: d.nozzles.map((n) => ({
         id: `nz-${n.kind}`,
         side: n.side,
         offset: n.offset,
         kind: n.kind,
      })),
   };
}

/** Equipment palette — 7 standard types. */
export const EQUIPMENT_PALETTE = [
   { type: "vessel",         label: "Vessel",      tag_prefix: "V"  },
   { type: "pump",           label: "Pump",        tag_prefix: "P"  },
   { type: "heat_exchanger", label: "Exchanger",   tag_prefix: "E"  },
   { type: "column",         label: "Column",      tag_prefix: "C"  },
   { type: "tank",           label: "Tank",        tag_prefix: "TK" },
   { type: "compressor",     label: "Compressor",  tag_prefix: "K"  },
   { type: "reactor",        label: "Reactor",     tag_prefix: "R"  },
];

/** Generate a unique tag for a new equipment given existing tags. */
export function generateTag(prefix, existingTags) {
   const taken = new Set(existingTags || []);
   for (let i = 101; i < 10000; i++) {
      const tag = `${prefix}-${i}`;
      if (!taken.has(tag)) return tag;
   }
   return `${prefix}-${Date.now()}`;
}
